import type { Hero, Position, PositionRates } from '../types';

const POSITIONS: Position[] = [1, 2, 3, 4, 5];

export interface FormationPrediction {
  heroId: number;
  position: Position;
  confidence: number;
  probabilities: PositionRates;
  manual: boolean;
}

export interface FormationResult {
  predictions: FormationPrediction[];
  score: number;
  conflicts: Position[];
}

function fallbackRates(hero: Hero): PositionRates {
  const weights = POSITIONS.map((position) => {
    const index = hero.positions.indexOf(position);
    return index < 0 ? 1 : Math.max(10, 70 - index * 15);
  });
  const total = weights.reduce((sum, value) => sum + value, 0);
  return Object.fromEntries(POSITIONS.map((position, index) => [position, (weights[index] / total) * 100])) as PositionRates;
}

function normalizedRates(hero: Hero): PositionRates {
  const source = hero.positionRates ?? fallbackRates(hero);
  const values = POSITIONS.map((position) => Math.max(0, Number(source[position]) || 0));
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return fallbackRates(hero);
  return Object.fromEntries(POSITIONS.map((position, index) => [position, (values[index] / total) * 100])) as PositionRates;
}

function assignmentsFor(count: number): Position[][] {
  const output: Position[][] = [];
  const visit = (current: Position[], remaining: Position[]) => {
    if (current.length === count) {
      output.push(current);
      return;
    }
    remaining.forEach((position, index) => visit([...current, position], [...remaining.slice(0, index), ...remaining.slice(index + 1)]));
  };
  visit([], POSITIONS);
  return output;
}

export function inferFormation(heroes: Hero[], overrides: Record<number | string, Position> = {}): FormationResult {
  const team = heroes.slice(0, 5);
  const manualPositions = team.map((hero) => overrides[hero.id]).filter((value): value is Position => POSITIONS.includes(value));
  const conflicts = POSITIONS.filter((position) => manualPositions.filter((value) => value === position).length > 1);
  if (conflicts.length) return { predictions: [], score: 0, conflicts };
  if (!team.length) return { predictions: [], score: 0, conflicts: [] };

  const rates = team.map(normalizedRates);
  const candidates = assignmentsFor(team.length)
    .filter((assignment) => assignment.every((position, index) => !overrides[team[index].id] || overrides[team[index].id] === position))
    .map((assignment) => ({
      assignment,
      weight: assignment.reduce<number>((product, position, index) => product * Math.max(rates[index][position] / 100, 1e-9), 1),
    }));
  if (!candidates.length) return { predictions: [], score: 0, conflicts: manualPositions };

  const totalWeight = candidates.reduce((sum, candidate) => sum + candidate.weight, 0) || 1;
  const best = [...candidates].sort((a, b) => b.weight - a.weight || a.assignment.join('').localeCompare(b.assignment.join('')))[0];
  const predictions = team.map((hero, heroIndex) => {
    const probabilities = Object.fromEntries(POSITIONS.map((position) => {
      const weight = candidates.reduce((sum, candidate) => sum + (candidate.assignment[heroIndex] === position ? candidate.weight : 0), 0);
      return [position, (weight / totalWeight) * 100];
    })) as PositionRates;
    const position = best.assignment[heroIndex];
    return {
      heroId: hero.id,
      position,
      confidence: Number(probabilities[position].toFixed(1)),
      probabilities: Object.fromEntries(POSITIONS.map((p) => [p, Number(probabilities[p].toFixed(1))])) as PositionRates,
      manual: overrides[hero.id] === position,
    };
  });
  return { predictions, score: Number((best.weight / totalWeight * 100).toFixed(2)), conflicts: [] };
}

export const formationInternals = { fallbackRates, normalizedRates, assignmentsFor };
